import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import { lookupsJson } from '@ourbranch/lookups';

import Field from 'core/components/form/form.v2';

const currentYear = new Date().getFullYear();

const yearValidation = Yup.number()
  .required('Year is required')
  .min(1900, 'Year must be 1900 or later')
  .max(currentYear, `Year must be ${currentYear} or earlier`);

export const assetInitialValuesByType = {
  atv: {
    name: '',
    year: undefined,
    make: '',
    model: '',
    serialNumber: '',
    cc: undefined,
    description: ''
  },
  golfCart: {
    name: '',
    year: undefined,
    make: '',
    model: '',
    serialNumber: '',
    description: ''
  },
  unmannedAircraft: {
    name: '',
    year: undefined,
    make: '',
    model: '',
    serialNumber: '',
    weight: undefined,
    description: ''
  },
  boat: {
    name: '',
    classification: undefined,
    horsepower: undefined,
    year: undefined,
    lengthFt: undefined,
    mph: undefined,
    serialNumber: '',
    description: ''
  }
};

export const assetValidationSchemasByType = {
  atv: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    year: yearValidation,
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    serialNumber: Yup.string().required('Serial number is required'),
    cc: Yup.mixed().required('CC is required')
  }),
  golfCart: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    year: yearValidation,
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    serialNumber: Yup.string().required('Serial number is required')
  }),
  unmannedAircraft: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    year: yearValidation,
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    serialNumber: Yup.string().required('Serial number is required'),
    weight: Yup.number().required('Weight is required')
  }),
  boat: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    classification: Yup.string().required('Classification is required'),
    horsepower: Yup.number().required('Horsepower is required'),
    year: yearValidation,
    lengthFt: Yup.number().required('Length is required'),
    mph: Yup.number().required('Miles per hour is required'),
    serialNumber: Yup.string().required('Serial number is required')
  })
};

const NotOnPolicyAsset = ({ disabled }) => {
  const { values } = useFormikContext();
  const type = values.type;

  if (!type) {
    return null;
  }

  return (
    <>
      <Field xs={12} type="string" name="name" mode="dark" label="Name*" disabled={disabled} />

      {type === 'atv' && (
        <>
          <Field xs={4} type="numeric" name="year" mode="dark" label="Year*" disabled={disabled} />
          <Field xs={4} type="string" name="make" mode="dark" label="Make*" disabled={disabled} />
          <Field xs={4} type="string" name="model" mode="dark" label="Model*" disabled={disabled} />
          <Field xs={6} type="string" name="serialNumber" mode="dark" label="Serial number*" disabled={disabled} />
          <Field
            xs={6}
            type="select"
            name="cc"
            mode="dark"
            label="CC*"
            options={lookupsJson.farm.cc}
            disabled={disabled}
          />
          <Field xs={12} type="textarea" name="description" mode="dark" label="Description" disabled={disabled} />
        </>
      )}

      {type === 'golfCart' && (
        <>
          <Field xs={6} type="numeric" name="year" mode="dark" label="Year*" disabled={disabled} />
          <Field xs={6} type="string" name="make" mode="dark" label="Make*" disabled={disabled} />
          <Field xs={6} type="string" name="model" mode="dark" label="Model*" disabled={disabled} />
          <Field xs={6} type="string" name="serialNumber" mode="dark" label="Serial number*" disabled={disabled} />
          <Field xs={12} type="textarea" name="description" mode="dark" label="Description" disabled={disabled} />
        </>
      )}

      {type === 'unmannedAircraft' && (
        <>
          <Field xs={6} type="numeric" name="year" mode="dark" label="Year*" disabled={disabled} />
          <Field xs={6} type="string" name="make" mode="dark" label="Make*" disabled={disabled} />
          <Field xs={6} type="string" name="model" mode="dark" label="Model*" disabled={disabled} />
          <Field xs={6} type="string" name="serialNumber" mode="dark" label="Serial number*" disabled={disabled} />
          <Field xs={12} type="numeric" name="weight" mode="dark" label="Weight (lbs)*" disabled={disabled} />
          <Field xs={12} type="textarea" name="description" mode="dark" label="Description" disabled={disabled} />
        </>
      )}

      {type === 'boat' && (
        <>
          <Field
            xs={6}
            type="select"
            name="classification"
            mode="dark"
            label="Classification*"
            options={lookupsJson.farm.boatClassifications}
            disabled={disabled}
          />
          <Field xs={6} type="numeric" name="horsepower" mode="dark" label="Horsepower*" disabled={disabled} />
          <Field xs={6} type="numeric" name="year" mode="dark" label="Year*" disabled={disabled} />
          <Field xs={6} type="numeric" name="lengthFt" mode="dark" label="Length (ft)*" disabled={disabled} />
          <Field xs={6} type="numeric" name="mph" mode="dark" label="Miles per hour*" disabled={disabled} />
          <Field xs={6} type="string" name="serialNumber" mode="dark" label="Serial number*" disabled={disabled} />
          <Field xs={12} type="textarea" name="description" mode="dark" label="Description" disabled={disabled} />
        </>
      )}
    </>
  );
};

NotOnPolicyAsset.propTypes = {
  disabled: PropTypes.bool.isRequired
};

export default NotOnPolicyAsset;
